/**
 * useXRSession - React hook for XR session management
 * 
 * Uses web-core events to control XR:
 * - Emits 'xr-start' / 'xr-stop' commands
 * - Listens to 'xr-started' / 'xr-stopped' / 'xr-exited' responses
 * 
 * This decouples UI from the app implementation.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { XRSessionState } from '@core/events/events';
import emitter from '@webcore/core/events/eventemitter';

// Re-export for convenience
export { XRSessionState };

export interface UseXRSessionOptions {
  /** Whether the app content is loaded and ready */
  isLoaded?: boolean;
  /** Whether running in EyeJack App Clip */
  isInAppClip?: boolean;
  /** Auto-start XR when in App Clip and loaded */
  autoStartInAppClip?: boolean;
}

export interface UseXRSessionReturn {
  /** Current session state */
  state: XRSessionState;
  
  // Derived state flags
  isIdle: boolean;
  isReady: boolean;
  isStarting: boolean;
  isRunning: boolean;
  isStopping: boolean;
  isError: boolean;
  
  /** Whether the user has manually exited XR (for App Clip flow) */
  hasExited: boolean;
  
  /** Error message if in error state */
  errorMessage: string | null;
  
  // Actions
  /** Start XR session */
  start: () => void;
  /** Stop XR session */
  stop: () => void;
  /** Toggle XR session */
  toggle: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useXRSession(options: UseXRSessionOptions = {}): UseXRSessionReturn {
  const { 
    isLoaded = false, 
    isInAppClip = false, 
    autoStartInAppClip = true,
  } = options;

  const [state, setState] = useState<XRSessionState>(XRSessionState.IDLE);
  const [hasExited, setHasExited] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Track if we've already attempted auto-start
  const hasAutoStarted = useRef(false);
  // Set up event listeners
  useEffect(() => {
    const onStarted = () => {
      setState(XRSessionState.RUNNING);
      setErrorMessage(null);
    };

    const onStopped = () => {
      setState(XRSessionState.READY);
    };

    const onExited = () => {
      setState(XRSessionState.READY);
      setHasExited(true);
    };

    const onErrorUndefined = () => {
      setState(XRSessionState.ERROR);
      setErrorMessage('XR is not available on this device.');
    };

    const onErrorNotSupported = () => {
      setState(XRSessionState.ERROR);
      setErrorMessage('AR is not supported on this device or browser.');
    };

    const onErrorPermission = () => {
      setState(XRSessionState.ERROR);
      setErrorMessage('Camera permission was denied.');
    };

    emitter.on('xr-started', onStarted);
    emitter.on('xr-stopped', onStopped);
    emitter.on('xr-exited', onExited);
    emitter.on('xr-error-undefined', onErrorUndefined);
    emitter.on('xr-error-not-supported', onErrorNotSupported);
    emitter.on('xr-error-permission-denied', onErrorPermission);

    return () => {
      emitter.off('xr-started', onStarted);
      emitter.off('xr-stopped', onStopped);
      emitter.off('xr-exited', onExited);
      emitter.off('xr-error-undefined', onErrorUndefined);
      emitter.off('xr-error-not-supported', onErrorNotSupported);
      emitter.off('xr-error-permission-denied', onErrorPermission);
    };
  }, []);

  // Update state to ready when loaded
  useEffect(() => {
    if (isLoaded && state === XRSessionState.IDLE) {
      setState(XRSessionState.READY);
    }
  }, [isLoaded, state]);

  // Auto-start logic for App Clip
  useEffect(() => {
    if (!isLoaded || !isInAppClip || !autoStartInAppClip) return;
    if (hasAutoStarted.current || hasExited) return;
    if (state !== XRSessionState.READY) return;

    hasAutoStarted.current = true;
    setState(XRSessionState.STARTING);
    emitter.emit('xr-start');
  }, [isLoaded, isInAppClip, autoStartInAppClip, hasExited, state]);

  // Start XR
  const start = useCallback(() => {
    if (state === XRSessionState.RUNNING || state === XRSessionState.STARTING) return;

    setState(XRSessionState.STARTING);
    setErrorMessage(null);
    setHasExited(false);
    emitter.emit('xr-start');
  }, [state]);

  // Stop XR
  const stop = useCallback(() => {
    if (state !== XRSessionState.RUNNING) return;

    setState(XRSessionState.STOPPING);
    emitter.emit('xr-stop');
    
    // Mark as exited for App Clip flow
    if (isInAppClip) {
      setHasExited(true);
    }
  }, [state, isInAppClip]);

  // Toggle XR
  const toggle = useCallback(() => {
    if (state === XRSessionState.RUNNING) {
      stop();
    } else if (state === XRSessionState.READY) {
      start();
    }
  }, [state, start, stop]);

  return {
    state,
    isIdle: state === XRSessionState.IDLE,
    isReady: state === XRSessionState.READY,
    isStarting: state === XRSessionState.STARTING,
    isRunning: state === XRSessionState.RUNNING,
    isStopping: state === XRSessionState.STOPPING,
    isError: state === XRSessionState.ERROR,
    hasExited,
    errorMessage,
    start,
    stop,
    toggle,
  };
}
